/**
 * Share / Add Users picker dialog.
 * Loads users from mock OIDC via GET /api/extension-invites/users.
 */

import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import { SERIF_HEADING } from '../theme'
import {
  createExtensionInvites,
  listInviteUsers,
  type InviteType,
  type PickerUser,
} from '../pages/extensionInvitesApi'

export interface ShareUsersDialogProps {
  open: boolean
  onClose: () => void
  apiBaseUrl: string
  token: string | null
  /** Current signed-in email (excluded from picker). */
  selfEmail: string | null
  type: InviteType
  definitionId: string
  displayName: string
  onSent: (count: number) => void
}

export function ShareUsersDialog({
  open,
  onClose,
  apiBaseUrl,
  token,
  selfEmail,
  type,
  definitionId,
  displayName,
  onSent,
}: ShareUsersDialogProps) {
  const [users, setUsers] = useState<PickerUser[]>([])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!open) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      setSelected(new Set())
      setSearch('')
      try {
        const list = await listInviteUsers(apiBaseUrl, token)
        if (!cancelled) setUsers(list)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, apiBaseUrl, token])

  const selfNorm = (selfEmail || '').trim().toLowerCase()
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return users.filter((u) => {
      if (selfNorm && u.email.toLowerCase() === selfNorm) return false
      if (!q) return true
      return (
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      )
    })
  }, [users, search, selfNorm])

  const toggle = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(email)) next.delete(email)
      else next.add(email)
      return next
    })
  }

  const title = type === 'share_blueprint' ? 'Share' : 'Add Users'
  const help =
    type === 'share_blueprint'
      ? `Choose who should get "${displayName}". They will get a notification and can accept a copy into their own library.`
      : `Invite people to "${displayName}" as viewers. They will get a notification to accept membership.`

  const handleSend = async () => {
    if (selected.size === 0) return
    setSending(true)
    setError(null)
    try {
      const result = await createExtensionInvites(apiBaseUrl, token, {
        type,
        definitionId,
        displayName,
        toUserEmails: [...selected],
      })
      onSent(result.invites?.length ?? selected.size)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => !sending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ ...SERIF_HEADING, fontSize: '1.1rem' }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {help}
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search people…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
        />
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {filtered.map((u) => {
              const checked = selected.has(u.email)
              return (
                <ListItemButton key={u.email} onClick={() => toggle(u.email)} dense>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={u.name}
                    secondary={u.email}
                    primaryTypographyProps={{ sx: { fontSize: '0.875rem', fontWeight: 600 } }}
                    secondaryTypographyProps={{ sx: { fontSize: '0.75rem' } }}
                  />
                </ListItemButton>
              )
            })}
            {!loading && filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                No users found. Is mock OIDC running on :4700?
              </Typography>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || selected.size === 0}
        >
          {sending ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
          {type === 'share_blueprint' ? 'Share' : 'Send invites'}
          {selected.size > 0 ? ` (${selected.size})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
